import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CustomCode, isLegacyCustomCode } from '@superfunnel/types';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  getCustomCodeDelayDisplay,
  getCustomCodeLocationDisplay,
} from '@superfunnel/utils';

interface CustomCodeCardProps {
  customCode: CustomCode;
  source: 'account' | 'dynamicPage' | 'website';
  isInherited?: boolean;
  isOverridden?: boolean;
  displayEnabled?: boolean;
  onToggle?: (codeId: string, enabled: boolean) => void;
  onEdit?: (code: CustomCode) => void;
  onDelete?: (codeId: string) => void;
  readOnly?: boolean;
}

const getSourceStyles = (
  source: 'account' | 'dynamicPage' | 'website',
): { bg: string; text: string } => {
  switch (source) {
    case 'account':
      return { bg: 'bg-[#f2f4f7]', text: 'text-[#414e62]' };
    case 'dynamicPage':
      return { bg: 'bg-blue-50', text: 'text-blue-700' };
    case 'website':
      return { bg: 'bg-green-50', text: 'text-green-700' };
  }
};

const getSourceDisplayName = (
  source: 'account' | 'dynamicPage' | 'website',
): string => {
  switch (source) {
    case 'account':
      return 'Account';
    case 'dynamicPage':
      return 'Dynamic Page';
    case 'website':
      return 'Website';
  }
};

export default function CustomCodeCard({
  customCode,
  source,
  isInherited,
  isOverridden,
  displayEnabled,
  onToggle,
  onEdit,
  onDelete,
  readOnly,
}: CustomCodeCardProps) {
  const enabled = displayEnabled ?? customCode.enabled;
  const canEdit = !readOnly && !isInherited;
  const sourceStyles = getSourceStyles(source);

  const locationDisplay = getCustomCodeLocationDisplay(customCode);
  const delayDisplay = getCustomCodeDelayDisplay(customCode);
  const locationInfo = [locationDisplay, delayDisplay]
    .filter(Boolean)
    .join(' / ');

  // Get preview code for display (new format or legacy)
  const previewCode = isLegacyCustomCode(customCode)
    ? customCode.code
    : [customCode.headCode, customCode.bodyCode].filter(Boolean).join('\n\n');

  return (
    <div
      className="flex flex-col gap-2 rounded-xl border border-[#e4e7ec] bg-white px-3 pt-2 pb-3"
      style={{ boxShadow: '0px 1px 2px 0 rgba(21,28,36,0.05)' }}
    >
      <div className="flex items-start justify-between py-1 pl-1">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-[#141c25]">
              {customCode.name}
            </p>
            <div className="flex items-center gap-1.5">
              <div
                className={`flex items-center justify-center rounded-md px-[5px] pt-[1.5px] pb-0.5 ${sourceStyles.bg}`}
              >
                <p className={`text-[13px] ${sourceStyles.text}`}>
                  {getSourceDisplayName(source)}
                </p>
              </div>
              {isOverridden && (
                <div className="flex items-center justify-center rounded-md bg-[#f4f0ff] px-[5px] pt-[1.5px] pb-0.5">
                  <p className="text-[13px] text-[#2b1f84]">Overridden</p>
                </div>
              )}
              {isInherited && (
                <div className="flex items-center justify-center rounded-md bg-[#f2f4f7] px-[5px] pt-[1.5px] pb-0.5">
                  <p className="text-[13px] text-[#414e62]">Inherited</p>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-[#637083]">{locationInfo}</p>
        </div>

        <div className="flex items-center gap-4">
          {canEdit && (
            <>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => onEdit?.(customCode)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
                >
                  <PencilIcon className="h-[18px] w-[18px] text-[#637083]" />
                </button>
                <button
                  onClick={() => onDelete?.(customCode.id)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
                >
                  <TrashIcon className="h-[18px] w-[18px] text-[#FF4D4D]" />
                </button>
              </div>
              <div className="h-5 w-px bg-[#E4E7EC]" />
            </>
          )}
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => onToggle?.(customCode.id, checked)}
            disabled={!onToggle}
            size="sm"
          />
        </div>
      </div>

      <div
        className="rounded-2lg bg-gray-50 p-3"
        style={{ boxShadow: '0px 1px 2px 0 rgba(21,28,36,0.05)' }}
      >
        <pre className="max-h-32 overflow-auto text-sm whitespace-pre-wrap text-[#141c25]">
          {previewCode}
        </pre>
      </div>
    </div>
  );
}
